import { maintenanceApiRoutes } from "../constants/maintenance.constants";
import {
  MaintenanceHistoryEntry,
  MaintenanceWindowQuery,
  CreateMaintenanceWindowRequest,
  UpdateMaintenanceWindowRequest,
} from "../types/maintenance.types";

import { API_TAGS, baseApi } from "@/app/constants/apiTags.constants";
import { REQUEST_METHODS } from "@/app/constants/global-constants";
import { buildQueryParams } from "@/utils/form/queryParams";
import { buildApiRoute } from "@/utils/navigation/buildApiRoute";

type MaintenanceActionResponse = GeneralResponse<Record<string, unknown>>;

export const maintenanceApi = baseApi.injectEndpoints({
  overrideExisting: false,

  endpoints: (builder) => ({
    getCurrentMaintenanceWindow: builder.query<
      MaintenanceHistoryEntry | null,
      void
    >({
      query: () => ({
        url: buildApiRoute(maintenanceApiRoutes.getCurrentMaintenanceWindow),
      }),
      providesTags: [API_TAGS.MAINTENANCE.CURRENT],
      transformResponse: (
        response: APIResponse<MaintenanceHistoryEntry | null>,
      ) => response?.data,
    }),

    getMaintenanceWindows: builder.query<
      GeneralResponse<MaintenanceHistoryEntry[]>,
      MaintenanceWindowQuery
    >({
      query: ({ filters }) => ({
        url: buildApiRoute(maintenanceApiRoutes.getMaintenanceWindows),
        params: buildQueryParams(filters),
      }),
      providesTags: [API_TAGS.MAINTENANCE.WINDOWS],
    }),

    createMaintenanceWindow: builder.mutation<
      MaintenanceActionResponse,
      { data: CreateMaintenanceWindowRequest }
    >({
      query: ({ data }) => ({
        url: buildApiRoute(maintenanceApiRoutes.createMaintenanceWindow),
        method: REQUEST_METHODS.POST,
        body: data,
      }),
      invalidatesTags: [API_TAGS.MAINTENANCE.WINDOWS],
    }),

    updateMaintenanceWindow: builder.mutation<
      MaintenanceActionResponse,
      { id: string; data: UpdateMaintenanceWindowRequest }
    >({
      query: ({ id, data }) => ({
        url: buildApiRoute(maintenanceApiRoutes.updateMaintenanceWindow, {
          id,
        }),
        method: REQUEST_METHODS.PATCH,
        body: data,
      }),
      invalidatesTags: [API_TAGS.MAINTENANCE.WINDOWS],
    }),

    cancelMaintenanceWindow: builder.mutation<
      MaintenanceActionResponse,
      { id: string }
    >({
      query: ({ id }) => ({
        url: buildApiRoute(maintenanceApiRoutes.cancelMaintenanceWindow, {
          id,
        }),
        method: REQUEST_METHODS.POST,
      }),
      invalidatesTags: [API_TAGS.MAINTENANCE.WINDOWS],
    }),
  }),
});

export const {
  useGetCurrentMaintenanceWindowQuery,
  useGetMaintenanceWindowsQuery,
  useCreateMaintenanceWindowMutation,
  useUpdateMaintenanceWindowMutation,
  useCancelMaintenanceWindowMutation,
} = maintenanceApi;
